import { useCallback, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { addMonths, describe, isDay, monthEnd, monthStart, today } from '../../dates';
import { useLanguage } from '../../contexts/language';
import { usePatientDiary } from '../../hooks/patient-diary';

import { DateNav } from '../date-nav';
import { DayCard } from '../day-card';
import { LoadFailed } from '../load-failed';
import { MonthChart } from '../month-chart';
import { PatientHeader } from '../patient-header';
import { SavePdf } from '../save-pdf';

import styles from './style.module.css';

// A patient's Diary tab, as the blueprint's Patient diary draws it (#89): the
// month chart of their daily records on every screen size, and under it the
// selected day's record, read only. Accepting the invitation shared the whole
// diary; nothing here writes.
//
// The selected day lives in the URL (?day=), so a reload or Back returns to
// it; a missing, malformed or future day means today. One request reads the
// selected day's month, which Save as PDF exports (#85).
export function PatientDiary() {
  const { text, language } = useLanguage();
  const { patientId } = useParams();
  const [ parameters, setParameters ] = useSearchParams();
  const [ now ] = useState(today);

  let day = parameters.get('day');

  if (isDay(day) === false || day > now) {
    day = now;
  }

  const { data, error, retry } = usePatientDiary({ patientId, from: monthStart(day), to: monthEnd(day) });

  const onSelect = useCallback(function(next) {
    setParameters({ day: next });
  }, [ setParameters ]);

  const onPreviousMonth = useCallback(function() {
    onSelect(addMonths({ day, months: -1 }));
  }, [ day, onSelect ]);

  // The month after can still be partly in the past: its days up to today
  // are reachable, and the selection stops at today.
  const onNextMonth = useCallback(function() {
    let next = addMonths({ day, months: 1 });

    if (next > now) {
      next = now;
    }

    onSelect(next);
  }, [ day, now, onSelect ]);

  if (error != null) {
    return <LoadFailed onRetry={ retry }/>;
  }

  if (data === undefined) {
    return null;
  }

  const days = data.patientDailyRecords;
  const selected = days.find(function({ date }) {
    return date === day;
  });

  let card = null;

  if (selected != null) {
    card = <DayCard day={ selected } readOnly/>;
  }

  return (
    <section className={ styles.page }>
      <PatientHeader patient={ data.patient }/>

      <div className={ styles.month }>
        <DateNav
          level={ 2 }
          title={ describe({ day, language, month: true }) }
          previousLabel={ text.previousMonth }
          nextLabel={ text.nextMonth }
          onPrevious={ onPreviousMonth }
          onNext={ onNextMonth }
          canGoNext={ monthEnd(day) < now }
        />

        <SavePdf href={ `/reports/patients/${ patientId }/diary/${ day.slice(0, 7) }.pdf` }/>
      </div>

      <MonthChart days={ days } selected={ day } today={ now } onSelect={ onSelect } onPhones/>

      { card }
    </section>
  );
}
